import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import { CONSTANTS } from "./constant";
import CustomDialog from "../../../library/common/CustomDialog";
import { AuthorListing } from "../../../library/component";
import styles from "./Authors.module.scss";
import SearchBar from "../../../library/common/SearchBar/SearchBar";
import { api } from "../../../services";
import { throttle } from "lodash";
import FileUploadPreview from "../../../library/common/FileUploadPreview";

const useFileUpload = (setUpdatedData, setErrors) => {
  const handleFileChange = useCallback(
    (e) => {
      const { name, files } = e.target;

      if (files?.length > 0) {
        const file = files[0];

        // Check if file is larger than 10MB (10 * 1024 * 1024 bytes)
        if (file.size > 10 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            [name]:
              "Please upload a file up to 10MB only, or you can share the book file url",
          }));
          setUpdatedData((prev) => ({ ...prev, [name]: null }));
          return;
        } else {
          setErrors((prev) => ({ ...prev, [name]: "" })); // clear error
        }

        setUpdatedData((prev) => ({ ...prev, [name]: file }));
      } else {
        setUpdatedData((prev) => ({ ...prev, [name]: null }));
      }
    },
    [setUpdatedData, setErrors]
  );

  return { handleFileChange };
};

const link =
  "https://res.cloudinary.com/dam9jxmjj/image/upload/v1754935088/stag/uploads/banner_images/home/689a2f2f23134992c25cefdd.jpg";

const {
  ALL_AUTHORS_TITLE,
  ADD_NEW_AUTHOR_BUTTON,
  SEARCH_AUTHORS_LABEL,
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  CANCEL_BUTTON,
  ADD_BUTTON,
  THROTTLE_DURATION,
  ACCEPTED_IMAGE_TYPES,
} = CONSTANTS;

const Authors = () => {
  const authors = useRef([]);
  const [filteredAuthors, setFilteredAuthors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [newAuthor, setnewAuthor] = useState({
    name: {
      en: "",
      hi: "",
    },
  });
  // const [editingId, setEditingId] = useState(null);
  const [editedAuthor, setEditedAuthor] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { handleFileChange } = useFileUpload(setEditedAuthor, setErrors);

  const getFieldValue = useCallback(
    (fieldPath) => {
      const keys = fieldPath.split(".");
      let value = editedAuthor[fieldPath];
      if (value === undefined) {
        value = keys.reduce(
          (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
          "initialData"
        );
      }
      return value;
    },
    [editedAuthor]
  );

  const filterAuthors = useCallback(() => {
    if (authors.current?.length !== 0) {
      setFilteredAuthors(
        authors.current?.filter(
          (item) =>
            item?.name?.en
              ?.toLowerCase()
              ?.includes(searchText?.trim()?.toLowerCase()) ||
            item?.name?.hi?.includes(searchText?.trim())
        )
      );
    }
  }, [searchText]);

  const getAuthors = useCallback(async () => {
    try {
      const { data } = await api.get("/sects");
      authors.current = data;
      filterAuthors();
    } catch (error) {
      console.error("Failed to fetch authors", error);
    }
  }, [filterAuthors]);

  useEffect(() => {
    getAuthors();
  }, [getAuthors]);

  useEffect(() => {
    filterAuthors();
  }, [filterAuthors]);

  const handleEditClicked = (id) => {
    const author = authors.current?.find((item) => item?._id === id);
    setEditedAuthor(author);
  };

  const handleAddNewAuthor = () => {
    setModalOpen(true);
    setErrors({});
  };

  const handleOpenDeleteDialog = (item) => {
    setAuthorToDelete(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setAuthorToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteSector = throttle(async () => {
    try {
      setIsLoading(true);
      const _id = authorToDelete?._id;
      await api.delete("/sects/" + _id);
      authors.current = authors.current?.filter(
        (sector) => sector?._id !== _id
      );
      filterAuthors();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleCloseModal = () => {
    setModalOpen(false);
    setErrors({});
    setnewAuthor({ name: { en: "", hi: "" } });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setnewAuthor((prev) => ({
      ...prev,
      name: { ...prev.name, [name]: value },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newAuthor?.name?.en?.trim())
      newErrors.name = { en: "Name in english is required" };
    if (!newAuthor?.name?.hi?.trim())
      newErrors.name = { hi: "Name in hindi is required" };
    return newErrors;
  };

  const handleAddSector = throttle(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        setIsLoading(true);
        await api.post("/sects", newAuthor);
        await getAuthors();
        handleCloseModal();
      } catch (error) {
        console.error("Failed to post: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, THROTTLE_DURATION);

  const handleSaveClicked = throttle(async () => {
    try {
      setIsLoading(true);
      const id = editedAuthor?._id;
      await api.patch(`/sects/${id}`, {
        name: editedAuthor?.name,
      });
      authors.current = authors.current?.map((sect) =>
        sect?._id === id ? { ...sect, ...editedAuthor } : sect
      );
    } catch (error) {
      console.error("Failed to update: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEditedInputChange = (e) => {
    const { name, value } = e.target;
    setEditedAuthor((prev) => ({
      ...prev,
      name: { ...prev.name, [name]: value },
    }));
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  return (
    <Paper className={styles.paper_container}>
      <Typography
        variant="h5"
        className={styles.header_title}
        textAlign={"center"}
      >
        {ALL_AUTHORS_TITLE}
      </Typography>

      {/* Add new button */}
      <div className={styles.search_add_container}>
        <Button variant="contained" onClick={handleAddNewAuthor}>
          {ADD_NEW_AUTHOR_BUTTON}
        </Button>

        {/* Search bar */}
        <SearchBar
          placeholderText={SEARCH_AUTHORS_LABEL}
          updateSearchText={handleSearch}
        />
      </div>

      {/* items */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={80}></TableCell>
              <TableCell>Name (En)</TableCell>
              <TableCell>Name (Hi)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAuthors.map((item, index) => (
              <AuthorListing
                key={`author-${item?._id}`}
                index={index}
                editedAuthor={editedAuthor}
                englishName={item?.name?.en || ""}
                hindiName={item?.name?.hi || ""}
                imageUrl={link}
                // imageUrl={item?.imageUrl || ""}
                inputChange={handleEditedInputChange}
                saveClick={handleSaveClicked}
                editClick={() => handleEditClicked(item?._id)}
                deleteClick={() => handleOpenDeleteDialog(item)}
                disablePrimary={isLoading}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* add new dialog */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{ADD_NEW_AUTHOR_BUTTON}</DialogTitle>
        <DialogContent>
          <FileUploadPreview
            id="author-image-upload"
            allowUrl={false}
            acceptedTypes={ACCEPTED_IMAGE_TYPES}
            fileField="authorImage"
            urlField="author.image"
            getFieldValue={getFieldValue}
            onFileChange={handleFileChange}
            onRemove={() =>
              handleFileChange({
                target: { name: "authorImage", value: null },
              })
            }
            onUrlChange={handleInputChange}
            error={errors?.authorImage || errors?.["author.image"]}
            urlLabel="link of author image"
          />
          <TextField
            fullWidth
            margin="dense"
            label="English Name"
            name="en"
            value={newAuthor?.name?.en ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.en}
            helperText={errors?.name?.en}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Hindi Name"
            name="hi"
            value={newAuthor?.name?.hi ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name?.hi}
            helperText={errors?.name?.hi}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{CANCEL_BUTTON}</Button>
          <Button
            onClick={handleAddSector}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {ADD_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>

      {/* delete dialog */}
      <CustomDialog
        open={openDeleteDialog}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialog}
        onPrimaryClick={handleDeleteSector}
        onSecondaryClick={handleCloseDeleteDialog}
        disablePrimary={isLoading}
      />
    </Paper>
  );
};

export default Authors;
